import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Switch } from "@headlessui/react";
import "animate.css/animate.min.css";
import AssessmentDropdown from "../components/AssessmentDropdown";
import BackArrow from "../assets/images/back-arrow.svg";
import FaceDiagram from "../assets/icons/face-match.svg";
import DgmerIcon from "../assets/icons/dgmer-icon.svg";
import heartIcon from "../assets/images/heart_logo_unity.png";
import IconLive from "../assets/icons/live-icon.svg";
import IconScan from "../assets/icons/scan-icon.svg";
import ProfileIcon from "../assets/health-icon/profile_icon.svg";
import FamilyIcon from "../assets/health-icon/family.svg";
import GeneralIcon from "../assets/health-icon/general.svg";
import MedicalIcon from "../assets/health-icon/medical.png";
import MedicationIcon from "../assets/health-icon/medication.png";
import SymptomsIcon from "../assets/health-icon/Symptoms.svg";
import WomenIcon from "../assets/health-icon/women.svg";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/auth";
// import Rhinitis from "../assets/images/Rhinitis.svg";

const SelectAssesmentSwitch = ({ enabled, setEnabled, disabled = false }) => (
  <Switch
    checked={enabled}
    onChange={setEnabled}
    disabled={disabled}
    className={`shrink-0 inline-flex items-center h-4 w-9 p-0.5 rounded-full bg-[#464646] transition-colors duration-300`}
  >
    <span className="sr-only">{enabled ? "Disable" : "Enable"}</span>
    <div
      className={`h-3 w-3 p-0.5 rounded-full  ${
        enabled ? "translate-x-5 bg-[#68D628]" : "translate-x-0 bg-[#9E0D2F]"
      } transition-transform duration-300`}
    ></div>
  </Switch>
);

const ConfigureAssessments = () => {
  const { state } = useLocation();
  const [assessments, setAssessments] = useState({
    ...state.selectedAssesments,
    faceMatch: false,
    dgmer: false,
  });
  const initialMinutes = parseInt(
    (state?.estimatedTime || "~4 minutes").replace(/[^0-9]/g, "")
  );
  const digiMerEnabled = assessments.dgmer;

  const finalEstimatedTime = digiMerEnabled
    ? `~${initialMinutes + 3} minutes`
    : `~${initialMinutes} minutes`;

  const livelinessDetectionDuration = (value) => {
    switch (value) {
      case "High Sensitivity":
        return 45;
      case "Low Sensitivity":
        return 55;
      case "Disabled":
        return 60;
      default:
        return 55;
    }
  };

  const scanStrictnessDuration = (value) => {
    switch (value) {
      case "Very Low":
        return 1;
      case "Low":
        return 2;
      case "Moderate":
        return 3;
      case "High":
        return 4;
      case "Very High":
        return 5;
      default:
        return 3;
    }
  };

  const [liveliness, setLiveliness] = useState("Low Sensitivity");
  const [strictness, setStrictness] = useState("Moderate");

  const strictnessLabel = strictness.toLowerCase();

  const [livelinessDuration, setLivelinessDuration] = useState(
    livelinessDetectionDuration("Low Sensitivity")
  );
  const [strictnessDuration, setStrictnessDuration] = useState(
    scanStrictnessDuration("Moderate")
  );

  const toggle = (key) => (v) => setAssessments((a) => ({ ...a, [key]: v }));

  const handleLivelinessChange = (value) => {
    setLiveliness(value);
    const duration = livelinessDetectionDuration(value);
    setLivelinessDuration(duration);
  };

  const handleStrictnessChange = (value) => {
    setStrictness(value);
    const duration = scanStrictnessDuration(value);
    setStrictnessDuration(duration);
  };
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  const [step, setStep] = useState(1);

  const [dgmerAnswers, setDgmerAnswers] = useState({
    q1: "",
    q2: "",
    q3: "",
    q4: "",
    q5: "",
    q6: "",
    q7: "",
    q8: "",
    q9: "",
    q10: "",
    q11: "",
    q12: "",
    q13: "",
    q14: "",
  });

  const navigate = useNavigate();
  const navigateToScan = () => {
    // "/digimer-assessment";
    navigate("/all-scan-instructions", {
      state: {
        selectedAssesments: assessments,
        livelinessDuration,
        strictness: strictnessDuration,
        dgmerAnswers,
        digiMerEnabled: assessments.dgmer,
      },
    });
  };
  const { userData } = useAuth();
  const gender = userData?.gender?.toLowerCase();

  const totalQuestions = {
    1: 7,
    2: 4,
    3: gender === "female" ? 3 : 2,
  };

  const step1Answers = [
    dgmerAnswers.q1,
    dgmerAnswers.q2,
    dgmerAnswers.q3,
    dgmerAnswers.q4,
    dgmerAnswers.q5,
    dgmerAnswers.q6,
    dgmerAnswers.q7,
  ];

  const step2Answers = [
    dgmerAnswers.q8,
    dgmerAnswers.q9,
    dgmerAnswers.q10,
    dgmerAnswers.q11,
  ];

  const step3Answers =
    gender === "female"
      ? [dgmerAnswers.q12, dgmerAnswers.q13, dgmerAnswers.q14]
      : [dgmerAnswers.q12, dgmerAnswers.q14];

  const answeredStep1 = step1Answers.filter((x) => x !== "").length;
  const answeredStep2 = step2Answers.filter((x) => x !== "").length;
  const answeredStep3 = step3Answers.filter((x) => x !== "").length;

  let completedStepsQuestions = 0;

  if (step === 1) {
    completedStepsQuestions = answeredStep1;
  } else if (step === 2) {
    completedStepsQuestions = totalQuestions[1] + answeredStep2;
  } else {
    completedStepsQuestions =
      totalQuestions[1] + totalQuestions[2] + answeredStep3;
  }

  // Total required questions
  const totalAllQuestions =
    totalQuestions[1] + totalQuestions[2] + totalQuestions[3];

  // Final percentage
  const progressPercent = Math.min(
    100,
    Math.round((completedStepsQuestions / totalAllQuestions) * 100)
  );
  const canProceed = () => {
    if (step === 1) {
      return step1Answers.every((x) => x !== "");
    }
    if (step === 2) {
      return step2Answers.every((x) => x !== "");
    }
    if (step === 3) {
      return step3Answers.every((x) => x !== "");
    }
    return false;
  };

  const [errorMsg, setErrorMsg] = useState("");

  const Question = ({ label, field, options }) => {
    const handleSelect = (val) =>
      setDgmerAnswers((prev) => ({ ...prev, [field]: val }));

    const isThree = options?.length === 3;

    return (
      <div className="space-y-4">
        <h2 className="text-[12px] text-slate-300 font-medium">{label}</h2>

        {!isThree && (
          <div className="inline-flex bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl  p-1">
            {["Yes", "No"].map((choice) => {
              const active = dgmerAnswers[field] === choice;
              return (
                <button
                  key={choice}
                  onClick={() => handleSelect(choice)}
                  className={`px-6 py-2 rounded-xl text-[12px] transition-all duration-300 transform ${
                    active
                      ? "scale-[1.02] bg-[#3A3A3A] text-white"
                      : "scale-100 text-[#A0A4AB]"
                  }`}
                >
                  {choice}
                </button>
              );
            })}
          </div>
        )}

        {isThree && (
          <div className="flex gap-8 flex-wrap text-[11px]">
            {options.map((choice) => {
              const selected = dgmerAnswers[field] === choice;
              return (
                <label
                  key={choice}
                  className="flex items-center gap-2 cursor-pointer"
                  style={{ lineHeight: "15px" }}
                >
                  <div className="relative w-5 h-5 flex items-center justify-center">
                    <input
                      type="radio"
                      name={field}
                      value={choice}
                      checked={selected}
                      onChange={() => handleSelect(choice)}
                      className="sr-only"
                    />
                    <div
                      className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                        selected ? "border-blue-400" : "border-slate-600"
                      }`}
                    >
                      {selected && (
                        <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                      )}
                    </div>
                  </div>

                  <span className="text-slate-300 font-medium capitalize">
                    {choice}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-[calc(100dvh-6rem)] bg-black px-6 pt-0 lg:pb-20 md:pb-20 pb-10 relative z-50 responsive-assment">
      <div className="tab-screen">
        <div className="flex justify-start items-center relative mb-8 xl:justify-start">
          <Link
            className=" w-9 h-9 bg-[#2D2D2D] rounded-full flex flex-row justify-center items-center"
            to={-1}
          >
            <img src={BackArrow} alt="" />
          </Link>
          <h4 className="text-lg font-medium lg:text-2xl md:text-2xl text-white ml-4">
            UnityHealth
          </h4>
        </div>
        <div className="flex justify-center items-center">
          <img
            src={heartIcon}
            alt="Heart Icon"
            className="w-[220px] object-contain"
          />
        </div>
        <div className="w-11/12 mx-auto bg-[#1D1D1D]/70 rounded-[20px] px-6 pt-8 pb-4 relative flex flex-col mt-6">
          <h3 className="text-[#8AB2DF] text-base font-semibold text-center mb-1">
            Digital Health Screening
          </h3>
          <p className="text-center text-xxs font-light text-white mb-7">
            Configure your assessment
          </p>
          <div className="flex flex-col justify-between items-start px-3">
            <AssessmentDropdown
              icon={IconLive}
              title="Liveliness Detection"
              description="Verifies that the user is a real, live person during the scan."
              value={liveliness}
              setValue={handleLivelinessChange}
              options={["Disabled", "High Sensitivity", "Low Sensitivity"]}
            />

            <div className="my-2"></div>

            <AssessmentDropdown
              icon={IconScan}
              title="Scan Strictness"
              description="Controls how strictly the system validates scan quality and conditions."
              value={strictness}
              setValue={handleStrictnessChange}
              options={["Very Low", "Low", "Moderate", "High", "Very High"]}
            />
          </div>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              {
                id: "faceMatch",
                icon: FaceDiagram,
                line1: "Face Match (PIVC)",
                desc: "Confirms the user’s identity by matching their face with the provided reference image.",
              },
              {
                id: "dgmer",
                icon: DgmerIcon,
                line1: "Digi MER",
                desc: "Digital medical examination completed remotely through an automated, guided journey.",
              },
            ].map((c) => (
              <div key={c.id} className="relative">
                <div className="flex justify-between items-start px-3">
                  <div className="flex items-start">
                    <img
                      src={c.icon}
                      className="w-6 assessment-icon"
                      alt={c.id}
                    />
                    <div className="ml-2">
                      <h4 className="text-[10px] font-semibold text-white mb-1 leading-3">
                        {c.line1}
                        <br />
                        <span className="tracking-tight">{c.line2}</span>
                      </h4>
                      <p className="flex-1 text-[0.5625rem] text-white/70 leading-3 mb-2">
                        {c.desc}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <SelectAssesmentSwitch
                      enabled={assessments[c.id]}
                      setEnabled={toggle(c.id)}
                    />
                  </div>
                </div>
              </div>
            ))}
            <div className="bg-[#1F766D] rounded-[5px] px-3 py-2 mt-1 mx-3">
              <p className="text-[9px] text-white font-light text-justify leading-tight">
                <span className=" font-semibold">Note:</span> Estimated time to
                complete the selected assessments: {finalEstimatedTime} minutes.
                Higher assessment failure rates can be expected when liveness
                detection operates at high sensitivity and scan strictness is
                configured to {strictnessLabel} settings.
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              if (assessments.dgmer) {
                setShowQuestionnaire(true);
              } else {
                navigateToScan();
              }
            }}
            className="block w-9/12 mx-auto mt-6 py-2 rounded-full bg-white text-black text-center"
          >
            Proceed
          </button>
        </div>
        <div className="w-full bg-[#364560] rounded-[20px] px-6 py-5 mt-3">
          <h6 className="text-sm font-medium text-[#8AB2DF] mb-2">
            Disclaimer :
          </h6>
          <p className="text-[9px] font-light leading-[14px] text-justify">
            For Investigational Use Only. Careplix is not a substitute for the
            clinical judgment of a health care professional. Careplix is
            intended to improve your awareness of general wellness. Careplix
            does not diagnose, treat, mitigate or prevent any disease, symptom,
            disorder or abnormal physical state. Consult with a health care
            professional or emergency services if you believe you may have a
            medical issue.
          </p>
        </div>
      </div>
      {showQuestionnaire && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col animate__animated animate__fadeIn animate__faster">
          <div className="flex-1 overflow-y-auto px-4 pt-4 pb-24">
            <header className="mb-4 space-y-4">
              <div className="flex items-center gap-3">
                <Link
                  className="w-9 h-9 bg-[#2D2D2D] rounded-full flex justify-center items-center"
                  to={-1}
                >
                  <img src={BackArrow} alt="" className="w-4 h-4" />
                </Link>

                {/* Title + Subtitle */}
                <div className="flex flex-col">
                  <h2 className="text-[20px] font-semibold text-white leading-tight">
                    Digi MER
                  </h2>
                  <p className="text-[13px] text-white/60 leading-tight">
                    Questionnaire (Global Standard)
                  </p>
                </div>
              </div>

              {/* Progress */}
              <div>
                <div className="flex items-center justify-between text-[12px] text-white/55 mb-1">
                  <span>Progress</span>
                  <span>
                    <span>{progressPercent}%</span>{" "}
                  </span>
                </div>

                <div className="h-1.5 rounded-full bg-[#202020] overflow-hidden">
                  <div
                    className="h-full bg-[#2563EB] transition-all duration-500 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            </header>

            {step === 1 && (
              <>
                <div className="animate__animated animate__fadeInUp animate__faster">
                  {/* Personal & Lifestyle */}
                  <section className="mb-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center">
                        <img
                          src={ProfileIcon}
                          className="w-10 h-10 opacity-100"
                        />
                      </div>
                      <h2 className="text-[20px] font-semibold text-white">
                        Personal & Lifestyle
                      </h2>
                    </div>

                    <div className="bg-[#161616] rounded-2xl px-6 py-6 space-y-8 shadow-sm">
                      <Question
                        label="Do you smoke or use any tobacco products?"
                        field="q1"
                      />
                      <Question
                        label="Do you consume alcohol?"
                        field="q2"
                        options={["never", "occasionally", "frequently"]}
                      />
                      <Question
                        label="Recreational drugs in last 12 months?"
                        field="q3"
                      />
                    </div>
                  </section>

                  {/* Medical History */}
                  <section className="mb-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center">
                        <img
                          src={MedicalIcon}
                          className="w-10 h-10 opacity-100"
                        />
                      </div>
                      <h2 className="text-[20px] font-semibold text-white">
                        Medical History
                      </h2>
                    </div>

                    <div className="bg-[#161616] rounded-2xl px-6 py-6 space-y-6 shadow-sm">
                      <Question
                        label="High blood pressure, diabetes, or high cholesterol?"
                        field="q4"
                      />
                      <Question
                        label="Heart disease, stroke, or other heart-related condition?"
                        field="q5"
                      />
                      <Question
                        label="Asthma, COPD, or chronic lung disease?"
                        field="q6"
                      />
                      <Question
                        label="Kidney, liver, or thyroid disorders?"
                        field="q7"
                      />
                    </div>
                  </section>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="animate__animated animate__fadeInUp animate__faster">
                  <section className="mb-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center">
                        <img
                          src={SymptomsIcon}
                          className="w-10 h-10 opacity-100"
                        />
                      </div>
                      <h2 className="text-[20px] font-semibold text-white">
                        Recent Symptoms
                      </h2>
                    </div>

                    <div className="bg-[#161616] rounded-2xl px-6 py-6 space-y-6 shadow-sm">
                      <Question
                        label="Chest pain, breathlessness, or palpitations in last 3 months?"
                        field="q8"
                      />
                      <Question
                        label="Sudden weight loss, fatigue, or fever in last 3 months?"
                        field="q9"
                      />
                    </div>
                  </section>

                  <section className="mb-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center">
                        <img
                          src={MedicationIcon}
                          className="w-10 h-10 opacity-100"
                        />
                      </div>
                      <h2 className="text-[20px] font-semibold text-white">
                        Medication & Hospitalization
                      </h2>
                    </div>

                    <div className="bg-[#161616] rounded-2xl px-6 py-6 space-y-6 shadow-sm">
                      <Question
                        label="Currently taking any long-term medication?"
                        field="q10"
                      />
                      <Question
                        label="Hospitalized or had surgery in last 5 years?"
                        field="q11"
                      />
                    </div>
                  </section>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="animate__animated animate__fadeInUp animate__faster">
                  <section className="mb-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center">
                        <img
                          src={FamilyIcon}
                          className="w-10 h-10 opacity-100"
                        />
                      </div>
                      <h2 className="text-[20px] font-semibold text-white">
                        Family History
                      </h2>
                    </div>

                    <div className="bg-[#161616] rounded-2xl px-6 py-6 space-y-6 shadow-sm">
                      <Question
                        label="Family history of heart disease, stroke, diabetes, or cancer?"
                        field="q12"
                      />
                    </div>
                  </section>

                  {gender === "female" && (
                    <section className="mb-8">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="h-10 w-10 rounded-xl flex items-center justify-center">
                          <img
                            src={WomenIcon}
                            className="w-10 h-10 opacity-100"
                          />
                        </div>
                        <h2 className="text-[20px] font-semibold text-white">
                          Women Specific
                        </h2>
                      </div>

                      <div className="bg-[#161616] rounded-2xl px-6 py-6 space-y-6 shadow-sm">
                        <Question
                          label="Are you pregnant or undergoing fertility treatment?"
                          field="q13"
                        />
                      </div>
                    </section>
                  )}

                  <section className="mb-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center">
                        <img
                          src={GeneralIcon}
                          className="w-10 h-10 opacity-100"
                        />
                      </div>
                      <h2 className="text-[20px] font-semibold text-white">
                        General Health Declaration
                      </h2>
                    </div>

                    <div className="bg-[#161616] rounded-2xl px-6 py-6 space-y-6 shadow-sm">
                      <Question
                        label="Any other medical conditions not listed above?"
                        field="q14"
                      />
                    </div>
                  </section>
                </div>
              </>
            )}
          </div>

          <div className="fixed left-0 right-0 bottom-0 bg-black/95 border-t border-white/10 px-4 py-3">
            {errorMsg && (
              <p className="text-red-400 text-xs text-center mb-2">
                {errorMsg}
              </p>
            )}
            <button
              onClick={() => {
                if (!canProceed()) {
                  setErrorMsg("Please answer all questions to continue.");
                  return;
                }

                setErrorMsg("");

                if (step === 1) setStep(2);
                else if (step === 2) setStep(3);
                else {
                  setShowQuestionnaire(false);
                  navigateToScan();
                }
              }}
              className="w-full py-3 rounded-full bg-white text-black text-[14px] font-medium flex items-center justify-center gap-2"
            >
              {step < 3 ? "Continue" : "Submit"}
              <span className="text-[16px]">➜</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigureAssessments;
